/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { useEffect } from "react";
import dayjs from "dayjs";
import { Loader2 } from "lucide-react";
import { IProfile, IUser } from "../../../../store/api/admin/types";
import { getProfileList } from "../../../../pages/Administrations/helpers";
import { getFutureDate } from "../../../../utils/helpers";
import ConfirmDialog from "../../../Shared/Dialogs/ConfirmDialog";
import { DATEFORMATS, DIALOGOPENFOR, USERPASSWORD_MAXLENGTH } from "../../../../globals/globals";
import { useSelector } from "react-redux";
import { confirmDialogSelector, errorMsgSelector } from "../../../../store/helpers/helperSelectors";
import { IKeyValueAny } from "../../types";
import { usernameSelector } from "../../../../store/api/auth/selectors";
import { accessControlSettingsSelector } from "../../../../store/api/admin/selectors";

import { InputField } from "../../../Shared/Radix/Inputs/InputField";
import { PasswordField } from "../../../Shared/Radix/Inputs/PasswordField";
import { Label } from "../../../ui/label";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import { Select } from "../../../ui/select";
import { Sheet } from "../../../ui/sheet";
import { Field, FieldGroup, FieldLabel } from "../../../ui/field";
import { Icon } from "../../../Shared/Icon";
import DateRangeCalendarPicker from "../../../Shared/Calendar/DateRangeCalendarPicker";

interface IAddUpdateUserInfo {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profileData: IProfile[];
  errMsg: IKeyValueAny;
  setErrMsg: React.Dispatch<React.SetStateAction<IKeyValueAny>>;
  userFormData: IUser;
  setUserFormData: React.Dispatch<React.SetStateAction<IUser>>;
  passExpireDate: dayjs.Dayjs | null;
  setPassExpireDate: React.Dispatch<React.SetStateAction<dayjs.Dayjs | null>>;
  isChangePass: boolean;
  setIsChangePass: React.Dispatch<React.SetStateAction<boolean>>;
  submitHandler: () => Promise<boolean>;
  deleteHandler: () => void;
  handleClickAction: (actionName: string) => Promise<void>;
  isCreateUser: any;
  onBlurHandler: (event: any) => void;
  passNeverExpire: boolean;
  setPassNeverExpire: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadingStatus: any;
  cancelHandler: any;
  username: any;
  password: any;
  confirmPassword: any;
  passwordExpiryDate: any;
  firstName: any;
  lastName: any;
  department: any;
  location: any;
  phone: any;
  email: any;
}

const AddUpdateUserInfo: React.FC<IAddUpdateUserInfo> = ({
  open,
  onOpenChange,
  profileData,
  errMsg,
  setErrMsg,
  userFormData,
  setUserFormData,
  passExpireDate,
  setPassExpireDate,
  isChangePass,
  setIsChangePass,
  submitHandler,
  deleteHandler,
  handleClickAction,
  isCreateUser,
  onBlurHandler,
  passNeverExpire,
  setPassNeverExpire,
  isLoadingStatus,
  cancelHandler,
  username,
  password,
  confirmPassword,
  passwordExpiryDate,
  firstName,
  lastName,
  department,
  location,
  phone,
  email,
}) => {
  const today = new Date(Date.now());

  const openConfirmDialog = useSelector(confirmDialogSelector);
  const logedInUsername = useSelector(usernameSelector);
  const accessControlSettings = useSelector(accessControlSettingsSelector);
  const reduxErrorMsg = useSelector(errorMsgSelector);

  useEffect(() => {
    if (isCreateUser) {
      const el = document.getElementById(username) as HTMLInputElement;
      el?.focus();
    }
  }, [isCreateUser, username]);

  // Initialize password-related fields when editing a user or when sheet opens
  useEffect(() => {
    if (open && !isCreateUser) {
      // When editing existing user, ensure password-related fields are properly bound
      if (userFormData?.passExpireDate) {
        setPassExpireDate(dayjs(userFormData.passExpireDate));
      }
      // Set passNeverExpire: if passExpireDate is 0 then checked, else unchecked
      setPassNeverExpire(userFormData?.passExpireDate === 0);
    }
  }, [open, isCreateUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear error messages when the sheet opens
  useEffect(() => {
    if (open) {
      setErrMsg({});
    }
  }, [open, setErrMsg]);

  /* ---- Field-level change handler (InputField signature: name, value) ---- */
  const onFieldChange = (name: string, value: string) => {
    setUserFormData({ ...userFormData, [name]: value });

    if (name === "confirmPassword") {
      if (userFormData?.password !== value) {
        setErrMsg({ ...errMsg, [name]: "Passwords do not match" });
      } else {
        setErrMsg({ ...errMsg, [name]: "" });
      }
    } else {
      setErrMsg({ ...errMsg, [name]: "" });
    }
    if (name === "profileId") {
      if (value === "0") {
        setErrMsg({ ...errMsg, [name]: "Invalid Profile. Please Select Profile" });
      } else {
        setErrMsg({ ...errMsg, [name]: "" });
      }
    }
  };

  /* ---- Password field change handler ---- */
  const onPasswordChange = (name: string, value: string) => {
    setUserFormData({ ...userFormData, [name]: value });
    setErrMsg({ ...errMsg, [name]: "" });
  };

  /* ---- Confirm password change handler ---- */
  const onConfirmPasswordChange = (name: string, value: string) => {
    setUserFormData({ ...userFormData, [name]: value });
    if (userFormData?.password !== value) {
      setErrMsg({ ...errMsg, [name]: "Passwords do not match" });
    } else {
      setErrMsg({ ...errMsg, [name]: "" });
    }
  };

  /* ---- Profile select handler ---- */
  const handleProfileChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setUserFormData({ ...userFormData, profileId: parseInt(value, 10) });
    if (value === "0") {
      setErrMsg({ ...errMsg, profileId: "Invalid Profile. Please Select Profile" });
    } else {
      setErrMsg({ ...errMsg, profileId: "" });
    }
  };

  const handleChangeOnDatePicker = (newValue: dayjs.Dayjs | null) => {
    const newdate = dayjs(newValue).format(DATEFORMATS.ZONE);
    const expiryDate = new Date(newdate);
    if (expiryDate < today) {
      setErrMsg({ ...errMsg, passwordExpiryDate: "Expiry Date must not be in past." });
    } else if (expiryDate > getFutureDate(365)) {
      setErrMsg({ ...errMsg, passwordExpiryDate: "Expiry Date must not be more than 365 days from now." });
    } else {
      const timeInterval = expiryDate.getTime() - today.getTime();
      setUserFormData({ ...userFormData, passExpireDate: newdate, passExpireInterval: timeInterval });
      setPassExpireDate(newValue);
      setErrMsg({ ...errMsg, passwordExpiryDate: "" });
    }
  };

  const handleChangeOnPassNeverExpire = (checked: boolean) => {
    setPassNeverExpire(checked);
    const { passwordAgeDays, newdate, timeInterval } = getNewPasswordExpiryDetails();
    if (!checked) {
      setPassExpireDate(dayjs(getFutureDate(passwordAgeDays)));
      setUserFormData({ ...userFormData, passExpireDate: newdate, passExpireInterval: timeInterval, userExpired: checked });
    }
  };


  const getNewPasswordExpiryDetails = () => {
    const passwordAgeDays =
      accessControlSettings?.minimumAgeDays && accessControlSettings?.minimumAgeDays > 0
        ? accessControlSettings?.minimumAgeDays
        : 90;
    const newdate = dayjs(getFutureDate(passwordAgeDays)).format(DATEFORMATS.ZONE);
    const futureDate = new Date(newdate);
    const timeInterval = futureDate.getTime() - today.getTime();
    return { passwordAgeDays, newdate, timeInterval };
  };


  const handleCheckedChange = (name: string, checked: boolean) => {
    setUserFormData({ ...userFormData, [name]: checked });
  };

  const errCount = Object.values(errMsg).some((err) => err !== "");
  const disableSave = () => {
    if (errCount || isLoadingStatus?.user) return true;
    return false;
  };
  const isAdmin = !!userFormData?.username && userFormData?.username.toLowerCase() === "admin";
  const isAdminProfile =
    !isCreateUser && userFormData?.profileId === 1 && userFormData?.username.toLowerCase() === logedInUsername?.toLowerCase();

  const minPassLength = accessControlSettings?.minimumLength ?? 8;
  const passwordPattern = "^(?=.*[A-Z])(?=.*\\d)(?=.*[@#,$%^&*!?])[A-Za-z\\d@#,$%^&*!?]+$";
  const passwordPolicyTitle = `The password must be at least ${minPassLength} characters long and include at least one uppercase letter (A-Z), one digit (1-9), and one special non-alphabetic character (e.g.,?!%$#).`;

  const profileOptions = [
    { value: "", label: "Select" },
    ...getProfileList(profileData).map((p) => ({
      value: p.value,
      label: p.text,
    })),
  ];

  const slotProps = {
    textField: {
      fullWidth: true,
      size: "small" as const,
      variant: "outlined" as const,
      label: "",
      sx: {
        width: "100%",
        "& .MuiInputBase-root": {
          height: 40,
          backgroundColor: "var(--background)",
          transition: "all 0.2s ease",
        },
        "& .MuiInputBase-input": {
          padding: "10px 14px",
          color: "var(--foreground)",
          backgroundColor: "var(--background)",
          fontSize: "0.875rem",
          cursor: "pointer",
          fontFamily: "inherit",
          "&::placeholder": {
            color: "var(--muted-foreground)",
            opacity: 0.7,
          },
        },
        "& .MuiInputBase-input.Mui-disabled": {
          color: "var(--muted-foreground)",
          backgroundColor: "var(--background)",
          cursor: "not-allowed",
          opacity: 0.7,
        },
        "& .MuiInputLabel-root": {
          color: "var(--muted-foreground)",
          fontSize: "0.875rem",
        },
        "& .MuiInputLabel-root.Mui-focused": {
          color: "var(--foreground)",
        },
        "& .MuiInputLabel-root.Mui-disabled": {
          color: "var(--muted-foreground)",
          opacity: 0.7,
        },
        "& .MuiInputLabel-root.MuiInputLabel-shrink": {
          top: 0,
          transform: "translate(14px, -9px) scale(0.75)",
          backgroundColor: "var(--background)",
          paddingLeft: "4px",
          paddingRight: "4px",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--input)",
          borderWidth: "1px",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--foreground)",
        },
        "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--ring)",
          borderWidth: "2px",
        },
        "& .Mui-disabled .MuiOutlinedInput-notchedOutline": {
          borderColor: "var(--input)",
          borderWidth: "1px",
          opacity: 1,
        },
      },
    },
    openPickerButton: {
      sx: {
        color: "var(--muted-foreground)",
        "&:hover": { backgroundColor: "var(--secondary)" },
      },
    },
    popper: {
      sx: {
        zIndex: 9999,
        pointerEvents: "auto" as const,
        "& .MuiPaper-root": {
          backgroundColor: "var(--popover) !important",
          color: "var(--popover-foreground) !important",
          border: "1px solid var(--input)",
          boxShadow: "0 10px 25px rgba(0, 0, 0, 0.15)",
          pointerEvents: "auto" as const,
        },
        "& .MuiPickersDay-root": {
          color: "var(--foreground)",
          cursor: "pointer",
          pointerEvents: "auto" as const,
          "&:hover": {
            backgroundColor: "var(--muted)",
          },
          "&.Mui-selected": {
            backgroundColor: "var(--primary) !important",
            color: "var(--primary-foreground) !important",
          },
        },
        "& .MuiPickersCalendarHeader-label": {
          color: "var(--foreground)",
          fontWeight: 500,
        },
        "& .MuiIconButton-root": {
          color: "var(--muted-foreground)",
          cursor: "pointer",
          pointerEvents: "auto" as const,
          "&:hover": {
            backgroundColor: "var(--muted)",
          },
        },
        "& .MuiDayCalendar-weekDayLabel": {
          color: "var(--muted-foreground)",
          fontWeight: 600,
        },
        "& .MuiPickersYear-yearButton": {
          color: "var(--foreground)",
          cursor: "pointer",
          pointerEvents: "auto" as const,
          "&:hover": {
            backgroundColor: "var(--muted)",
          },
          "&.Mui-selected": {
            backgroundColor: "var(--primary) !important",
            color: "var(--primary-foreground) !important",
          },
        },
      },
    },
  };

  const footer = (
    <div className="flex w-full flex-wrap items-center justify-between gap-2">
      <div className="flex min-w-0 flex-1">
        {!isCreateUser && !isAdmin && (
          <Button
            type="button"
            variant="outline"
            className="gap-1.5 !text-destructive border-destructive/50 hover:bg-transparent hover:!text-destructive"
            onClick={() => deleteHandler()}
            disabled={isLoadingStatus?.user}
          >
            <Icon name="delete" size={18} />
            Delete user
          </Button>
        )}
      </div>
      <div className="flex flex-row gap-2">
        <Button type="button" variant="outline" onClick={() => { cancelHandler(); onOpenChange(false); }} disabled={isLoadingStatus?.user}>
          Cancel
        </Button>
        <Button
          onClick={async () => {
            if (isChangePass) {
              let hasErrors = false;
              const newErrMsg = { ...errMsg };
              if (!userFormData?.password) {
                newErrMsg[password] = "Password is required!.";
                hasErrors = true;
              }
              if (!userFormData?.confirmPassword) {
                newErrMsg[confirmPassword] = "Confirm Password is required!.";
                hasErrors = true;
              }
              if (hasErrors) {
                setErrMsg(newErrMsg);
                return;
              }
            }

            const success = await submitHandler();
            // Close sheet only after successful submit
            if (success) {
              setTimeout(() => {
                onOpenChange(false);
              }, 300);
            }
          }}
          disabled={disableSave()}
        >
          {isLoadingStatus?.user && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Save
        </Button>
      </div>
    </div>
  );

  const sheetSubtitle = isCreateUser
    ? "Create an account for AMS access. The profile controls permissions across nodes and administration."
    : isChangePass
      ? "Change the password for this user. The new password must meet the defined password policy requirements."
      : "Update identity and org context. Profile changes apply on the user’s next sign-in.";

  return (
    <>
      {openConfirmDialog === DIALOGOPENFOR.USER && (
        <ConfirmDialog actionName="Yes, Delete" handleClickAction={handleClickAction}>
          Are you sure you want to delete user <strong>{userFormData?.username || "Unknown"}</strong>?
        </ConfirmDialog>
      )}
      <Sheet
        open={open}
        onClose={() => { cancelHandler(); onOpenChange(false); }}
        title={isCreateUser ? "Add User" : isChangePass ? "Change Password" : "Edit User"}
        subtitle={sheetSubtitle}
        footer={footer}
        width="500px"
        useCardStyle={false}
        side="right"
      >


        <form autoComplete="off">
          {!isChangePass && (
            <>
              <div className="flex flex-col gap-1 mt-2">
                <h3 id="ac-user-details-heading" className="text-sm font-semibold text-foreground mb-0">
                  User details
                </h3>
                <p className="text-xs text-muted-foreground mt-0">Name, organization, and how to reach this person.</p>
              </div>
              <FieldGroup className="pt-4">
                {/* User Details Section */}
                <Field controlSize="lg">
                  <InputField
                    label="User Name"
                    name={username}
                    value={userFormData?.username}
                    autoFocus={isCreateUser}
                    disabled={isAdminProfile || !isCreateUser}
                    maxLength={100}
                    placeholder="Enter user name"
                    onChange={onFieldChange}
                    onBlur={onBlurHandler}
                    required
                    tooltipText="Maximum length: 100 characters"
                  />
                  {!reduxErrorMsg?.[username] && errMsg?.[username] && (
                    <p className="text-xs text-destructive m-0">{errMsg[username]}</p>
                  )}
                </Field>

                {(isCreateUser) && (
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <Field controlSize="md">
                      <PasswordField
                        label="Password"
                        name={password}
                        value={userFormData?.password || ""}
                        disabled={isCreateUser ? false : !isChangePass}
                        minLength={minPassLength}
                        maxLength={USERPASSWORD_MAXLENGTH}
                        placeholder="Enter password"
                        pattern={passwordPattern}
                        patternMsg="Please match the format requested."
                        passwordPolicyTitle={passwordPolicyTitle}
                        tooltipText={<>Minimum length: {minPassLength} characters.<br />Supported special characters: (,?!%$#)</>}
                        onChange={onPasswordChange}
                        onBlur={onBlurHandler}
                        required
                      />
                      {!reduxErrorMsg?.[password] && errMsg?.[password] && (
                        <p className="text-xs text-destructive m-0">{errMsg[password]}</p>
                      )}
                    </Field>

                    <Field controlSize="md">
                      <PasswordField
                        label="Confirm Password"
                        name={confirmPassword}
                        value={userFormData?.confirmPassword || ""}
                        required
                        disabled={isCreateUser ? false : !isChangePass}
                        minLength={minPassLength}
                        maxLength={USERPASSWORD_MAXLENGTH}
                        placeholder="Confirm password"
                        pattern={passwordPattern}
                        patternMsg="Please match the format requested."
                        passwordPolicyTitle={passwordPolicyTitle}
                        tooltipText={<>Minimum length: {minPassLength} characters.<br />Supported special characters: (,?!%$#)</>}
                        onChange={onConfirmPasswordChange}
                        onBlur={onBlurHandler}
                      />
                      {!reduxErrorMsg?.[confirmPassword] && errMsg?.[confirmPassword] && (
                        <p className="text-xs text-destructive m-0">{errMsg[confirmPassword]}</p>
                      )}
                    </Field>
                  </div>
                )}

                <Field controlSize="lg">
                  <FieldLabel htmlFor="profileId">Profile<span className="text-destructive ml-0.5">*</span></FieldLabel>
                  <Select
                    id="profileId"
                    name="profileId"
                    value={userFormData?.profileId ? userFormData.profileId.toString() : ""}
                    options={profileOptions}
                    onChange={handleProfileChange}
                    disabled={isAdmin || isAdminProfile}
                    className={errMsg?.profileId ? "!border-destructive" : ""}
                  />
                  {errMsg?.profileId && (
                    <p className="text-xs text-destructive m-0">{errMsg.profileId}</p>
                  )}
                </Field>

                {(!isAdminProfile) && (
                  <div className="flex flex-col gap-4">
                    {/* Password never expires */}
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="passNeverExpire"
                        checked={passNeverExpire}
                        onCheckedChange={(checked) => handleChangeOnPassNeverExpire(!!checked)}
                        disabled={isAdmin || isLoadingStatus?.user}
                        className="m-0"
                      />
                      <Label htmlFor={"passNeverExpire"} className="text-sm font-medium">
                        Password never expires
                      </Label>

                    </div>

                    {/* Expiration Date */}
                    <div className="space-y-2 w-full mt-3">
                      <Label className="mb-5">Expiration date</Label>
                      <DateRangeCalendarPicker
                        singleDate
                        fromValue={passNeverExpire ? null : passExpireDate}
                        onFromChange={(d) => handleChangeOnDatePicker(d)}
                        disabled={passNeverExpire}
                        triggerClassName="w-full"
                      />
                      {errMsg?.passwordExpiryDate && (
                        <p className="text-xs text-destructive m-0">{errMsg.passwordExpiryDate}</p>
                      )}
                    </div>

                    {/* Lock account */}
                    <div className="flex items-center gap-2 mt-3">
                      <Checkbox
                        id="banned"
                        checked={userFormData?.banned}
                        onCheckedChange={(checked) => handleCheckedChange("banned", !!checked)}
                        disabled={isCreateUser || isAdmin || isLoadingStatus?.user}
                        className="m-0"
                      />
                      <Label htmlFor={"banned"} className="text-sm font-medium">
                        Lock account
                      </Label>
                    </div>
                  </div>
                )}

                {/* User Information Fields */}
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field controlSize="md">

                    <InputField
                      label="First name"
                      name={firstName}
                      value={userFormData?.firstName}
                      maxLength={128}
                      placeholder="Enter first name"
                      onChange={onFieldChange}
                      tooltipText="Maximum length: 128 characters"
                    />
                  </Field>

                  <Field controlSize="md">
                    <InputField
                      label="Last name"
                      name={lastName}
                      value={userFormData?.lastName}
                      maxLength={128}
                      placeholder="Enter last name"
                      onChange={onFieldChange}
                      tooltipText="Maximum length: 128 characters"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field controlSize="md">
                    <InputField
                      label="Department"
                      name={department}
                      value={userFormData?.department}
                      maxLength={128}
                      placeholder="Enter department"
                      onChange={onFieldChange}
                      tooltipText="Maximum length: 128 characters"
                    />
                  </Field>

                  <Field controlSize="md">
                    <InputField
                      label="Location"
                      name={location}
                      value={userFormData?.location}
                      maxLength={128}
                      placeholder="Enter location"
                      onChange={onFieldChange}
                      tooltipText="Maximum length: 128 characters"
                    />
                  </Field>
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field controlSize="md">
                    <InputField
                      label="Phone"
                      name={phone}
                      value={userFormData?.phone}
                      maxLength={15}
                      placeholder="+1 (555) 000-0000"
                      onChange={onFieldChange}
                      onBlur={onBlurHandler}
                      tooltipText="Maximum length: 15 characters"
                    />
                    {errMsg?.[phone] && (
                      <p className="text-xs text-destructive m-0">{errMsg[phone]}</p>
                    )}
                  </Field>

                  <Field controlSize="md">
                    <InputField
                      label="Email"
                      name={email}
                      type="email"
                      value={userFormData?.email}
                      maxLength={128}
                      placeholder="name@example.com"
                      pattern={/^[^\s@]+@[^\s@]+\.[^\s@]+$/}
                      patternMsg="Please enter a valid email address."
                      onChange={onFieldChange}
                      onBlur={onBlurHandler}
                      tooltipText="Maximum length: 128 characters"
                    />
                    {errMsg?.[email] && (
                      <p className="text-xs text-destructive m-0">{errMsg[email]}</p>
                    )}
                  </Field>
                </div>
              </FieldGroup>

            </>)}

          {isChangePass && (
            <FieldGroup className="pt-2">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field controlSize="md">
                  <PasswordField
                    label="Password"
                    name={password}
                    value={userFormData?.password || ""}
                    disabled={false}
                    required
                    minLength={minPassLength}
                    maxLength={USERPASSWORD_MAXLENGTH}
                    placeholder="Enter password"
                    pattern={passwordPattern}
                    patternMsg="Please match the format requested."
                    passwordPolicyTitle={passwordPolicyTitle}
                    tooltipText={<>Minimum length: {minPassLength} characters.<br />Supported special characters: (,?!%$#)</>}
                    onChange={onPasswordChange}
                    onBlur={onBlurHandler}
                  />
                  {!reduxErrorMsg?.[password] && errMsg?.[password] && (
                    <p className="text-xs text-destructive m-0 mt-0">{errMsg[password]}</p>
                  )}
                </Field>

                <Field controlSize="md">
                  <PasswordField
                    label="Confirm Password"
                    name={confirmPassword}
                    value={userFormData?.confirmPassword || ""}
                    required
                    disabled={false}
                    minLength={minPassLength}
                    maxLength={USERPASSWORD_MAXLENGTH}
                    placeholder="Confirm password"
                    pattern={passwordPattern}
                    patternMsg="Please match the format requested."
                    passwordPolicyTitle={passwordPolicyTitle}
                    tooltipText={<>Minimum length: {minPassLength} characters.<br />Supported special characters: (,?!%$#)</>}
                    onChange={onConfirmPasswordChange}
                    onBlur={onBlurHandler}
                  />
                  {!reduxErrorMsg?.[confirmPassword] && errMsg?.[confirmPassword] && (
                    <p className="text-xs text-destructive m-0 mt-0">{errMsg[confirmPassword]}</p>
                  )}
                </Field>
              </div>
              {(!isAdminProfile) && (
                <>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="passNeverExpire"
                      checked={passNeverExpire}
                      onCheckedChange={(checked) => handleChangeOnPassNeverExpire(!!checked)}
                      disabled={isAdmin || isLoadingStatus?.user}
                      className="m-0"
                    />
                    <Label htmlFor={"passNeverExpire"} className="text-sm font-medium">
                      Password never expires
                    </Label>
                  </div>
                  <Field controlSize="md">
                    <FieldLabel htmlFor="profileId">Expiration date</FieldLabel>
                    <DateRangeCalendarPicker
                      singleDate
                      fromValue={passNeverExpire ? null : passExpireDate}
                      onFromChange={(d) => handleChangeOnDatePicker(d)}
                      disabled={passNeverExpire}
                      triggerClassName="w-full"
                    />
                    {errMsg?.passwordExpiryDate && (
                      <p className="text-xs text-destructive m-0">{errMsg.passwordExpiryDate}</p>
                    )}
                  </Field>
                </>
              )}
            </FieldGroup>





          )}
        </form>
      </Sheet>
    </>
  );
};

export default AddUpdateUserInfo;
